// src/app/CardFeedClient.jsx
"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { db } from "@/firebase/config";
import { useAuth } from "@/context/AuthContext";
import PrimaryTabs from "@/components/PrimaryTabs";
import PostFeed from "@/components/PostFeed";
import MyHubsFeed from "@/components/MyHubsFeed";
import HubFeed from "@/components/HubFeed";
import AnimatedTabPanel from "@/components/AnimatedTabPanel";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import PageContainer from "@/components/layout/PageContainer";
import SubnavBar from "@/components/layout/SubnavBar";
import { usePortal } from "@/context/PortalContext";
import AdsProvider from "@/components/ads/AdsProvider";
import { HOME_LATEST_AD_PLACEMENT } from "@/lib/ads/feedOpportunities.mjs";

const TABS = [
  { key: "latest", label: "Latest" },
  { key: "myhubs", label: "My Spaces" },
  { key: "hubs", label: "Explore Spaces" },
];
const POST_PAGE_SIZE = 20;
const HUB_LIST_LIMIT = 25;

export default function CardFeedClient() {
  const portal = usePortal();
  const { user } = useAuth();
  const allowedHubSlugs = portal?.allowedHubSlugs || null;
  const allowedHubKey = allowedHubSlugs?.join("|") || "";
  const feedAudienceId = portal?.id || "sproutly";
  const search = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  // Hide My Feed / My Hubs if logged out
  const visibleTabs = useMemo(() => {
    return TABS.filter((t) => (t.key === "myhubs" ? !!user : true));
  }, [user]);

  // Read tab from URL (?t= or ?tab=), normalize to a valid visible tab
  const urlTab = (search.get("t") || search.get("tab") || "latest").toLowerCase();
  const visibleKeys = useMemo(
    () => new Set(visibleTabs.map((t) => t.key)),
    [visibleTabs]
  );
  const replaceHomeTabUrl = useCallback((key) => {
    const params = new URLSearchParams(search.toString());
    params.set("t", key);
    params.delete("tab");
    const query = params.toString();
    const hash = typeof window === "undefined" ? "" : window.location.hash;
    router.replace(`${pathname || "/"}${query ? `?${query}` : ""}${hash}`, { scroll: false });
  }, [pathname, router, search]);
  const [requestedTab, setRequestedTab] = useState(urlTab);
  const active = visibleKeys.has(requestedTab) ? requestedTab : "latest";

  useEffect(() => {
    setRequestedTab(urlTab);
  }, [urlTab]);

  // Keep invalid or now-hidden URL tabs normalized through observable navigation.
  useEffect(() => {
    if (!visibleKeys.has(requestedTab)) {
      setRequestedTab("latest");
      replaceHomeTabUrl("latest");
    }
  }, [replaceHomeTabUrl, requestedTab, visibleKeys]);

  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      setRequestedTab((params.get("t") || params.get("tab") || "latest").toLowerCase());
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // When user clicks a tab, update local state immediately and mirror it in the URL.
  const handleTabChange = (key) => {
    setRequestedTab(key);
    replaceHomeTabUrl(key);
  };

  const activeChild = useMemo(() => {
    if (active === "latest") {
      return (
        <PostFeed
          scope="public"
          db={db}
          feedAudienceId={feedAudienceId}
          pageSize={POST_PAGE_SIZE}
          adPlacement={HOME_LATEST_AD_PLACEMENT}
        />
      );
    }
    if (active === "mine") return <MyFeedPlaceholder />;
    if (active === "hubs") {
      return (
        <HubFeed
          pageSize={HUB_LIST_LIMIT}
          allowedHubSlugs={allowedHubSlugs}
        />
      );
    }
    if (active === "myhubs") {
      return (
        <MyHubsFeed
          user={user}
          pageSize={HUB_LIST_LIMIT}
          allowedHubSlugs={allowedHubSlugs}
        />
      );
    }
    return null;
  }, [
    active,
    user,
    portal,
    feedAudienceId,
    allowedHubKey,
  ]);

  return (
    <AdsProvider enabled={active === "latest"}>
      <PageContainer topPadding={false}>
      {!portal ? (
        <SubnavBar>
          <PrimaryTabs tabs={visibleTabs} active={active} onChange={handleTabChange} />
        </SubnavBar>
      ) : null}
      <div className="mt-2 min-h-[60vh]">
        <AnimatedTabPanel activeKey={active} className="space-y-4">
          {activeChild}
        </AnimatedTabPanel>
      </div>
      </PageContainer>
    </AdsProvider>
  );
}

/* ================================
   Other tabs (placeholders)
   ================================ */

function MyFeedPlaceholder() {
  return (
    <div className="edge-mobile">
      <div className="card square-mobile text-center">
        <h4 className="font-semibold">My Feed</h4>
        <p className="mt-1 text-sm text-[var(--muted)]">
          Join spaces to build a personalized feed. (Server endpoint coming next.)
        </p>
      </div>
    </div>
  );
}
